import { Box, BoxProps, Flex, Stack } from '@chakra-ui/react'
import { LandingContainer } from 'components/common/LandingContainer'
import { Body1, H2 } from 'components/typography'
import { FC, ReactNode } from 'react'

type HomeSectionTone = 'light' | 'muted' | 'brand'

const TONES: Record<HomeSectionTone, Pick<BoxProps, 'bg' | 'color'>> = {
  light: { bg: 'alabaster', color: 'phthalo' },
  muted: { bg: 'grayscale.20', color: 'phthalo' },
  brand: { bg: 'medleve', color: 'alabaster' }
}

interface HomeSectionProps {
  /** Also the in-page anchor; the heading gets `${id}-title`. */
  id: string
  title: string
  tone?: HomeSectionTone
  /** Plain-text paragraphs rendered under the heading. */
  paragraphs?: readonly string[]
  /** Extra content for the second column (cards, lists…). */
  children?: ReactNode
}

/**
 * Text section of the organic homepage: `<section>` + `<h2>` + paragraphs, using the two-column
 * rhythm of the "Como funciona" block (heading left, content right on desktop; stacked on mobile).
 * Everything is in the server HTML — no lazy loading, no collapsed or hidden copy (PRD §38, §76).
 */
const HomeSection: FC<HomeSectionProps> = ({ id, title, tone = 'light', paragraphs = [], children }) => (
  <Box as="section" id={id} aria-labelledby={`${id}-title`} scrollMarginTop="88px" {...TONES[tone]}>
    <LandingContainer py={{ base: 12, lg: 24 }} maxW="1400px">
      <Flex direction={{ base: 'column', lg: 'row' }} px={{ lg: '60px' }} gap={{ base: 6, lg: 36 }}>
        <Box flex={1}>
          <H2 id={`${id}-title`}>{title}</H2>
        </Box>
        <Stack flex={1} spacing={4}>
          {paragraphs.map((paragraph) => (
            <Body1 key={paragraph}>{paragraph}</Body1>
          ))}
          {children}
        </Stack>
      </Flex>
    </LandingContainer>
  </Box>
)

export default HomeSection
